import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Icon from '../../components/AppIcon';
import UserLayout from '../user-dashboard/UserLayout';
import AgentLayout from '../agent-dashboard/AgentLayout';
import { selectCurrentUser } from '../../features/auth/authSlice';
import { isProfessionalRole } from '../../lib/authPortals';
import {
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
} from '../../features/api/apiSlice';

const PAGE_SIZE = 20;

const actionMeta = (type) => {
  switch (type) {
    case 'property_need':
      return { icon: 'ClipboardList', label: 'Property request', color: 'bg-violet-100 text-violet-700' };
    case 'need_response':
      return { icon: 'MessageSquare', label: 'Agent response', color: 'bg-sky-100 text-sky-700' };
    case 'response_status':
      return { icon: 'CheckCircle', label: 'Response update', color: 'bg-emerald-100 text-emerald-700' };
    case 'message':
      return { icon: 'Mail', label: 'Message', color: 'bg-amber-100 text-amber-800' };
    case 'property':
      return { icon: 'Building2', label: 'Listing', color: 'bg-indigo-100 text-indigo-700' };
    default:
      return { icon: 'Bell', label: 'Update', color: 'bg-slate-100 text-slate-700' };
  }
};

const NotificationsPage = () => {
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const isAgent = isProfessionalRole(user?.account_role);
  const Layout = isAgent ? AgentLayout : UserLayout;
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('all'); // all | unread

  const { data, isLoading, isFetching, refetch } = useGetNotificationsQuery({
    limit: PAGE_SIZE,
    offset,
    unread_only: filter === 'unread',
  });
  const [markRead] = useMarkNotificationsReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllNotificationsReadMutation();

  const items = data?.data || [];
  const total = data?.total || 0;
  const unread = data?.unread_count || 0;
  const hasMore = offset + items.length < total;

  const title = useMemo(() => 'Notifications', []);

  const openItem = async (item) => {
    if (!item.is_read) {
      try {
        await markRead({ ids: [item.id] }).unwrap();
      } catch {
        // continue
      }
    }
    if (item.link) navigate(item.link);
  };

  const content = (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">
            Stay on top of property requests, messages, and new activity.
          </p>
          {unread > 0 && (
            <p className="text-xs font-medium text-slate-600 mt-1">{unread} unread</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white">
            <button
              type="button"
              onClick={() => {
                setFilter('all');
                setOffset(0);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                filter === 'all' ? 'bg-slate-900 text-white' : 'text-slate-600'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => {
                setFilter('unread');
                setOffset(0);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                filter === 'unread' ? 'bg-slate-900 text-white' : 'text-slate-600'
              }`}
            >
              Unread
            </button>
          </div>
          {unread > 0 && (
            <button
              type="button"
              disabled={markingAll}
              onClick={async () => {
                await markAllRead();
                refetch();
              }}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading && (
          <div className="px-6 py-16 text-center text-slate-500 text-sm">Loading notifications…</div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="px-6 py-16 text-center">
            <Icon name="BellOff" size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="font-medium text-slate-700">You&apos;re all caught up</p>
            <p className="text-sm text-slate-500 mt-1">
              New property requests, agent replies, and messages will show up here.
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Link
                to="/notice-board"
                className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium"
              >
                Notice board
              </Link>
              <Link
                to="/property-search-browse"
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700"
              >
                Browse properties
              </Link>
            </div>
          </div>
        )}

        <ul className="divide-y divide-slate-100">
          {items.map((item) => {
            const meta = actionMeta(item.action_type);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => openItem(item)}
                  className={`w-full text-left px-4 sm:px-5 py-4 flex gap-3 hover:bg-slate-50 transition-colors ${
                    !item.is_read ? 'bg-sky-50/40' : ''
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${meta.color}`}
                  >
                    <Icon name={meta.icon} size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                        {meta.label}
                      </span>
                      {!item.is_read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                      )}
                      <span className="text-[11px] text-slate-400 ml-auto">{item.created}</span>
                    </div>
                    <p
                      className={`text-sm text-slate-900 mt-0.5 ${
                        !item.is_read ? 'font-semibold' : 'font-medium'
                      }`}
                    >
                      {item.title}
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{item.message}</p>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-slate-300 mt-3 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>

        {(hasMore || offset > 0) && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              disabled={offset === 0 || isFetching}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="text-sm font-medium text-slate-600 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-slate-400">
              {Math.min(offset + 1, total)}–{Math.min(offset + items.length, total)} of {total}
            </span>
            <button
              type="button"
              disabled={!hasMore || isFetching}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              className="text-sm font-medium text-slate-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Layout title={title} subtitle="Property requests, messages, and listings">
      {content}
    </Layout>
  );
};

export default NotificationsPage;
